import React, { useState } from "react";
import { Box, Container, useColorMode } from "@chakra-ui/react";
import { NavbarWithCallToAction } from "./chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "./chakra/FooterWithFourColumns/FooterWithFourColumns";
import { ModuleNavLauncher } from "./ModuleNavLauncher";
import { LayoutChromeContext } from "./LayoutChromeContext";
import { ModuleConfig } from "../types/ModuleConfig";
import { getColor } from "../brandConfig";
import { useAuth } from "../contexts/AuthContext";

// Re-export so `import { LayoutChromeContext } from "./ModulePageLayout"` keeps working.
// The context itself lives in the leaf file `LayoutChromeContext.tsx` so module-synced
// consumers (the calendars nav launcher) and this layout share ONE context — a page's
// `setBreadcrumbHidden(true)` reaches this provider instead of a private duplicate.
export { LayoutChromeContext };

interface ModulePageLayoutProps {
  moduleConfig: ModuleConfig;
  children: React.ReactNode;
  /** Container maxW - defaults to "container.xl" */
  maxW?: string;
  /** Container py - defaults to 8 */
  py?: number;
  /** Skip Container wrapper entirely (for pages that manage their own layout) */
  noContainer?: boolean;
  /** Skip the module nav launcher — public / immersive routes only (name kept from the retired breadcrumb bar) */
  noBreadcrumb?: boolean;
  /** Skip footer (full-bleed canvas pages) */
  noFooter?: boolean;
  /** Pin the frame to exactly 100dvh + overflow:hidden — the page owns its own
      internal scroll regions (e.g. knowledgeexplorer's 3 panes). */
  fillViewport?: boolean;
}

/**
 * ModulePageLayout — Shared layout wrapper for module pages.
 *
 * Provides consistent page scaffolding:
 *   1. NavbarWithCallToAction (top)
 *   2. ModuleNavLauncher with resolved permissions — the module's own navigation as a
 *      draggable floating launcher (replaced the sticky ModuleBreadcrumb bar 2026-09-03;
 *      vault/how-to/draggable-floating-launchers.md § Module-nav launcher)
 *   3. Container wrapping page content (configurable maxW)
 *   4. FooterWithFourColumns (bottom, pushed down by flex) — carries the
 *      "↻ Reset floating panels" failsafe, so every layout page has it for free
 *
 * Usage: Set `useLayout: true` on routes in moduleConfig.ts.
 * App.tsx wraps those routes with this component automatically, spreading the
 * route's `layoutOptions` as the individual props below and passing the RESOLVED
 * moduleConfig (with real permissions from backend).
 *
 * Page components should return ONLY their content — no Navbar, Footer,
 * launcher, Container, or outer Box wrappers.
 */
export const ModulePageLayout: React.FC<ModulePageLayoutProps> = ({
  moduleConfig,
  children,
  maxW = "container.xl",
  py = 8,
  noContainer = false,
  noBreadcrumb = false,
  noFooter = false,
  fillViewport = false,
}) => {
  const { colorMode } = useColorMode();
  const bgMain = getColor("background.main", colorMode);
  const { isAuthenticated } = useAuth();
  const [breadcrumbHidden, setBreadcrumbHidden] = useState(false);

  return (
    <LayoutChromeContext.Provider value={{ setBreadcrumbHidden }}>
      <Box
        bg={bgMain}
        display="flex"
        flexDirection="column"
        {...(fillViewport ? { h: "100dvh", overflow: "hidden" } : { minHeight: "100vh" })}
      >
        <NavbarWithCallToAction />
        {/* Module navigation is a DRAGGABLE FLOATING LAUNCHER, not the sticky ModuleBreadcrumb
            bar it replaced. `noBreadcrumb` / `breadcrumbHidden` keep their names so every caller
            and runtime hide (calendar Fit mode, the calendars nav launcher) works unchanged.
            Hidden for logged-out guests following a public shared link (e.g. /bill/:id). */}
        {!noBreadcrumb && !breadcrumbHidden && isAuthenticated && <ModuleNavLauncher moduleConfig={moduleConfig} />}
        {noContainer ? (
          <Box
            flex="1"
            {...(fillViewport ? { minH: 0, overflow: "hidden", display: "flex", flexDirection: "column" } : {})}
          >
            {children}
          </Box>
        ) : (
          <Container maxW={maxW} py={py} flex="1">
            {children}
          </Container>
        )}
        {!noFooter && <FooterWithFourColumns />}
      </Box>
    </LayoutChromeContext.Provider>
  );
};
